import {
  isLightColor,
  parseHexColor,
} from '@/components/basic/icon-badge/IconBadge';
import { teamBadgeIcon } from '@/views/settings/teams/teamIcon';
import {
  ARROW,
  ARROW_OFFSET,
  DISC,
  LETTER,
  LETTER_OFFSET,
  LOGO_NAVY,
  LOGO_TEAL,
  LOGO_VIEWBOX,
  RING,
} from './brandLogo';

// Bump when the logo or badge layout changes so cached PNGs are redrawn.
export const FAVICON_DESIGN_VERSION = '1';

export const FAVICON_SIZE = 64;
export const SYMBOL_FONT_FAMILY = 'Material Symbols Outlined';
export const DEFAULT_BADGE_COLOR = '#455A64';

const DARK_GLYPH = '#263238';
const LIGHT_GLYPH = '#FFFFFF';

// Sized for a 16px tab: the badge is ~45% of the icon so its color still
// reads after the browser scales 64px down to 16px.
const RING_RADIUS = 18;
const BADGE_RADIUS = 14.5;
const GLYPH_SIZE = 20;
const BADGE_CENTER = FAVICON_SIZE - RING_RADIUS;

export const teamBadgeColor = (team) => {
  const rgb = parseHexColor(team?.color);
  if (!rgb) return DEFAULT_BADGE_COLOR;
  return String(team.color).trim().toUpperCase();
};

const drawLogo = (ctx) => {
  const disc = new Path2D(DISC);

  ctx.save();
  ctx.scale(FAVICON_SIZE / LOGO_VIEWBOX, FAVICON_SIZE / LOGO_VIEWBOX);

  ctx.fillStyle = LOGO_TEAL;
  ctx.fill(disc);

  ctx.fillStyle = LOGO_NAVY;
  ctx.save();
  ctx.clip(disc);
  ctx.fill(new Path2D(RING));
  ctx.restore();

  ctx.save();
  ctx.translate(...LETTER_OFFSET);
  ctx.fill(new Path2D(LETTER), 'evenodd');
  ctx.restore();

  ctx.save();
  ctx.translate(...ARROW_OFFSET);
  ctx.fill(new Path2D(ARROW));
  ctx.restore();

  ctx.restore();
};

const drawBadge = (ctx, color) => {
  ctx.beginPath();
  ctx.arc(BADGE_CENTER, BADGE_CENTER, RING_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(BADGE_CENTER, BADGE_CENTER, BADGE_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  // A pale badge vanishes into its white ring on a light tab bar.
  if (isLightColor(color)) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(38, 50, 56, 0.45)';
    ctx.stroke();
  }
};

// Filled, heavier glyphs keep their shape at 16px; outlined ones turn to
// mush. Google serves a single-icon subset (~3KB) with CORS enabled.
const FILLED_SYMBOL_CSS =
  'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,600,1,0&display=block&icon_names=';
const GLYPH_NAME = /^[a-z0-9_]+$/;
const filledFonts = new Map();

const loadFilledSymbolFont = (glyph, doc, fetchFn) => {
  if (!GLYPH_NAME.test(glyph) || typeof FontFace === 'undefined') {
    return Promise.resolve(null);
  }
  if (!filledFonts.has(glyph)) {
    const load = (async () => {
      const css = await (await fetchFn(FILLED_SYMBOL_CSS + glyph)).text();
      const src = css.match(/url\((https:[^)]+)\)/)?.[1];
      if (!src) throw new Error(`No filled font for ${glyph}`);
      const family = `CY Favicon ${glyph}`;
      const face = new FontFace(family, `url(${src})`);
      await face.load();
      doc.fonts.add(face);
      return family;
    })().catch(() => {
      filledFonts.delete(glyph);
      return null;
    });
    filledFonts.set(glyph, load);
  }
  return filledFonts.get(glyph);
};

/**
 * Loads the app's outlined symbol font. Resolves false when it can't load,
 * so the ligature name is never drawn as plain text.
 */
export const loadSymbolFont = async (glyph, fonts = document.fonts) => {
  if (!fonts?.load) return false;
  try {
    const faces = await fonts.load(
      `${GLYPH_SIZE}px '${SYMBOL_FONT_FAMILY}'`,
      glyph
    );
    return faces.length > 0;
  } catch (e) {
    return false;
  }
};

const drawGlyph = (ctx, glyph, badgeColor, { family, filled }) => {
  const glyphColor = isLightColor(badgeColor) ? DARK_GLYPH : LIGHT_GLYPH;

  ctx.font = `${GLYPH_SIZE}px '${family}'`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = glyphColor;

  // Center on the glyph's ink box; symbol fonts sit off the em-box middle.
  const m = ctx.measureText(glyph);
  const x =
    BADGE_CENTER - (m.actualBoundingBoxRight - m.actualBoundingBoxLeft) / 2;
  const y =
    BADGE_CENTER + (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
  ctx.fillText(glyph, x, y);

  // The outlined fallback is ~2px strokes; thicken it so it survives 16px.
  if (!filled) {
    ctx.lineWidth = 1.25;
    ctx.strokeStyle = glyphColor;
    ctx.strokeText(glyph, x, y);
  }
};

const resolveGlyphFont = async (glyph, doc, fetchFn) => {
  const filledFamily = await loadFilledSymbolFont(glyph, doc, fetchFn);
  if (filledFamily) return { family: filledFamily, filled: true };
  if (await loadSymbolFont(glyph, doc.fonts)) {
    return { family: SYMBOL_FONT_FAMILY, filled: false };
  }
  return null;
};

const canvasToBlob = (canvas) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      blob ? resolve(blob) : reject(new Error('Favicon export failed'));
    }, 'image/png');
  });
};

/**
 * Draws the app logo with the team's badge.
 * `cacheable` is false unless the filled glyph drew, so a later attempt can
 * replace a fallback (outlined or glyph-less) icon.
 */
export const renderTeamFavicon = async (
  team,
  doc = document,
  fetchFn = (url) => fetch(url)
) => {
  const glyph = teamBadgeIcon(team);
  const color = teamBadgeColor(team);
  const glyphFont = await resolveGlyphFont(glyph, doc, fetchFn);

  const canvas = doc.createElement('canvas');
  canvas.width = FAVICON_SIZE;
  canvas.height = FAVICON_SIZE;
  const ctx = canvas.getContext('2d');

  drawLogo(ctx);
  drawBadge(ctx, color);
  if (glyphFont) drawGlyph(ctx, glyph, color, glyphFont);

  return {
    blob: await canvasToBlob(canvas),
    cacheable: !!glyphFont?.filled,
  };
};
